"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useProcessRowUpdate = void 0;
const react_1 = require("react");
const ramda_1 = require("ramda");
const use_reclassify_product_cell_hook_1 = require("./use-reclassify-product-cell.hook");
const use_update_enriched_attribute_cell_hook_1 = require("./use-update-enriched-attributes/use-update-enriched-attribute-cell.hook");
const hooks_1 = require("../../../../../client/hooks");
const product_1 = require("../../../../../client/product");
const product_table_constants_1 = require("../../../../../common/product-table/product-table.constants");
const table_columns_1 = require("../../../../../common/table-columns");
const attributes_1 = require("../../../../../constants/attributes");
const products_table_1 = require("../../../../../store/catalogs/products-table");
const table_config_1 = require("../../../../../store/shared/table-config");
const utils_1 = require("../../../../../utils");
const hooks_2 = require("../../../../../../hooks");
const SYNCED_CELL_VISIBLE_MS = 500;
const updateCellSet = (setCells, cellKey, shouldInclude) => {
    setCells((prev) => {
        const next = new Set(prev);
        if (shouldInclude) {
            next.add(cellKey);
        }
        else {
            next.delete(cellKey);
        }
        return next;
    });
};
const getUpdatedAttributeValues = (product, label, fallbackValues) => {
    const serverValues = product === null || product === void 0 ? void 0 : product.labels.edges.filter(({ node }) => node.label === label && product_1.VALID_STATUSES.includes(node.status)).map(({ node }) => node.value);
    const values = (serverValues === null || serverValues === void 0 ? void 0 : serverValues.length) ? serverValues : fallbackValues;
    return values.length ? values : [attributes_1.SpecialAttributeValue.Unknown];
};
const getVisibleAttributeValues = (values) => (values.length ? values : [attributes_1.SpecialAttributeValue.Unknown]);
const mergeUpdatedAttributeRow = (row, label, values, product) => {
    var _a, _b;
    const productMetadata = product === null || product === void 0 ? void 0 : product.productMetadata;
    return Object.assign(Object.assign({}, row), { [product_table_constants_1.LABEL_PATH]: Object.assign(Object.assign({}, row[product_table_constants_1.LABEL_PATH]), { [label]: values }), [attributes_1.CustomProductAttributes.LastAttributeUpdatedAt]: (_a = productMetadata === null || productMetadata === void 0 ? void 0 : productMetadata.lastAttributeUpdatedAt) !== null && _a !== void 0 ? _a : row[attributes_1.CustomProductAttributes.LastAttributeUpdatedAt], [attributes_1.CustomProductAttributes.LastEditedBy]: (_b = productMetadata === null || productMetadata === void 0 ? void 0 : productMetadata.lastEditedBy) !== null && _b !== void 0 ? _b : row[attributes_1.CustomProductAttributes.LastEditedBy] });
};
/**
 * Routes DataGrid row edits to the correct handler (classification vs enriched attributes).
 * Relies on MUI's built-in rollback by throwing on errors.
 */
const useProcessRowUpdate = ({ failedCellResetKey, selectedTreeNodePath, }) => {
    const { addError } = (0, hooks_2.useNotification)();
    const productTreeNodeId = (0, react_1.useMemo)(() => (0, utils_1.getTreeNodeIdFromPath)(selectedTreeNodePath), [selectedTreeNodePath]);
    const [loadingCells, setLoadingCells] = (0, react_1.useState)(new Set());
    const [syncedCells, setSyncedCells] = (0, react_1.useState)(new Set());
    const [failedCells, setFailedCells] = (0, react_1.useState)(new Set());
    const cellCommitQueueRef = (0, react_1.useRef)(new Map());
    const syncedCellTimersRef = (0, react_1.useRef)(new Map());
    const makeKey = (0, react_1.useCallback)((id, field) => `${id}:${field}`, []);
    const removeLoadingCell = (0, react_1.useCallback)((cellKey) => {
        updateCellSet(setLoadingCells, cellKey, false);
    }, []);
    const removeSyncedCell = (0, react_1.useCallback)((cellKey) => {
        updateCellSet(setSyncedCells, cellKey, false);
    }, []);
    const removeFailedCell = (0, react_1.useCallback)((cellKey) => {
        updateCellSet(setFailedCells, cellKey, false);
    }, []);
    const clearSyncedCellTimer = (0, react_1.useCallback)((cellKey) => {
        const timerId = syncedCellTimersRef.current.get(cellKey);
        if (timerId) {
            clearTimeout(timerId);
            syncedCellTimersRef.current.delete(cellKey);
        }
    }, []);
    const addLoadingCell = (0, react_1.useCallback)((cellKey) => {
        clearSyncedCellTimer(cellKey);
        removeFailedCell(cellKey);
        removeSyncedCell(cellKey);
        updateCellSet(setLoadingCells, cellKey, true);
    }, [clearSyncedCellTimer, removeFailedCell, removeSyncedCell]);
    const markCellSynced = (0, react_1.useCallback)((cellKey) => {
        clearSyncedCellTimer(cellKey);
        updateCellSet(setSyncedCells, cellKey, true);
        const timerId = setTimeout(() => {
            syncedCellTimersRef.current.delete(cellKey);
            removeSyncedCell(cellKey);
        }, SYNCED_CELL_VISIBLE_MS);
        syncedCellTimersRef.current.set(cellKey, timerId);
    }, [clearSyncedCellTimer, removeSyncedCell]);
    const markCellFailed = (0, react_1.useCallback)((cellKey) => {
        clearSyncedCellTimer(cellKey);
        removeSyncedCell(cellKey);
        updateCellSet(setFailedCells, cellKey, true);
    }, [clearSyncedCellTimer, removeSyncedCell]);
    (0, react_1.useEffect)(() => {
        setFailedCells(new Set());
    }, [failedCellResetKey]);
    (0, react_1.useEffect)(() => () => {
        syncedCellTimersRef.current.forEach(clearTimeout);
        syncedCellTimersRef.current.clear();
    }, []);
    const enqueueCellCommit = (0, react_1.useCallback)((cellKey, task) => {
        var _a;
        const previousCommit = (_a = cellCommitQueueRef.current.get(cellKey)) !== null && _a !== void 0 ? _a : Promise.resolve();
        const commit = previousCommit.catch(() => undefined).then(task);
        cellCommitQueueRef.current.set(cellKey, commit);
        const clearIfLatest = (isSuccessful) => {
            if (cellCommitQueueRef.current.get(cellKey) === commit) {
                cellCommitQueueRef.current.delete(cellKey);
                removeLoadingCell(cellKey);
                if (isSuccessful) {
                    markCellSynced(cellKey);
                }
                else {
                    markCellFailed(cellKey);
                }
            }
        };
        void commit.then(() => clearIfLatest(true), () => clearIfLatest(false));
        return commit;
    }, [markCellFailed, markCellSynced, removeLoadingCell]);
    const hasCell = (0, react_1.useCallback)((cells, params) => (cells.has(makeKey(params.id, params.field))), [makeKey]);
    const isCellLoading = (0, react_1.useCallback)((params) => (hasCell(loadingCells, params)), [hasCell, loadingCells]);
    const isCellSynced = (0, react_1.useCallback)((params) => (hasCell(syncedCells, params)), [hasCell, syncedCells]);
    const isCellFailed = (0, react_1.useCallback)((params) => (hasCell(failedCells, params)), [failedCells, hasCell]);
    const { refetchCounters } = (0, hooks_1.useRefetchCounters)();
    const { refetchProductList } = (0, hooks_1.useRefetchQueries)();
    const tableSort = (0, products_table_1.useProductsTableSort)();
    const tableFilter = (0, products_table_1.useProductsTableFilter)();
    const { reclassifyProductCell, isLoading: isReclassifying } = (0, use_reclassify_product_cell_hook_1.useReclassifyProductCell)({ selectedTreeNodePath });
    const { updateAttributeCell, isLoading: isUpdatingAttribute } = (0, use_update_enriched_attribute_cell_hook_1.useUpdateEnrichedAttributeCell)();
    const processRowUpdate = (0, react_1.useCallback)((newRow, oldRow) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g;
        const changedField = Object.keys(newRow).find((key) => !(0, ramda_1.equals)(newRow[key], oldRow[key]));
        if (!changedField) {
            return oldRow;
        }
        const parsedColumn = (0, table_columns_1.parseColumnId)(changedField);
        if (!parsedColumn) {
            throw new Error(`Unable to parse column id: ${changedField}`);
        }
        const cellKey = makeKey(newRow.id, changedField);
        addLoadingCell(cellKey);
        const { label, dataMeta } = parsedColumn;
        const { isAIEnriched } = dataMeta !== null && dataMeta !== void 0 ? dataMeta : {};
        let clearLoadingOnReturn = true;
        try {
            if (label === attributes_1.CustomProductAttributes.ClassificationProductType) {
                const previousCategory = (_b = (_a = oldRow[label]) !== null && _a !== void 0 ? _a : oldRow[changedField]) !== null && _b !== void 0 ? _b : '';
                const targetCategory = (_d = (_c = newRow[changedField]) !== null && _c !== void 0 ? _c : newRow[label]) !== null && _d !== void 0 ? _d : '';
                if (targetCategory === previousCategory) {
                    return oldRow;
                }
                yield reclassifyProductCell(newRow.id, targetCategory, previousCategory);
            }
            else if (isAIEnriched) {
                const nextValues = (_e = newRow[changedField]) !== null && _e !== void 0 ? _e : [];
                const prevValues = (_g = (_f = oldRow[product_table_constants_1.LABEL_PATH]) === null || _f === void 0 ? void 0 : _f[label]) !== null && _g !== void 0 ? _g : [];
                const optimisticRow = mergeUpdatedAttributeRow(newRow, label, getVisibleAttributeValues(nextValues), undefined);
                const commit = enqueueCellCommit(cellKey, () => (updateAttributeCell(newRow.id, nextValues, prevValues, label).then((updatedProduct) => {
                    const updatedValues = getUpdatedAttributeValues(updatedProduct, label, nextValues);
                    void refetchCounters({ selectedTreeNodeId: productTreeNodeId, attributeName: label });
                    const isSortedColumn = (tableSort === null || tableSort === void 0 ? void 0 : tableSort.key) === label;
                    const isFilteredColumn = (0, table_config_1.hasFilterForField)(tableFilter, changedField);
                    if (isSortedColumn || isFilteredColumn) {
                        void refetchProductList(productTreeNodeId);
                    }
                    return mergeUpdatedAttributeRow(optimisticRow, label, updatedValues, updatedProduct);
                })));
                clearLoadingOnReturn = false;
                return {
                    row: optimisticRow,
                    commit,
                    rollbackRow: oldRow,
                    onError: (error) => addError(error.message),
                };
            }
            else {
                throw new Error(`Unsupported editable column: ${changedField}`);
            }
            return newRow;
        }
        finally {
            if (clearLoadingOnReturn) {
                removeLoadingCell(cellKey);
            }
        }
    }), [
        productTreeNodeId,
        addError,
        addLoadingCell,
        enqueueCellCommit,
        makeKey,
        reclassifyProductCell,
        updateAttributeCell,
        refetchCounters,
        refetchProductList,
        removeLoadingCell,
        tableSort,
        tableFilter,
    ]);
    const processRowUpdateError = (0, react_1.useCallback)((error) => {
        addError(error.message);
    }, [addError]);
    return {
        processRowUpdate,
        processRowUpdateError,
        isProcessing: isReclassifying || isUpdatingAttribute,
        isCellLoading,
        isCellSynced,
        isCellFailed,
    };
};
exports.useProcessRowUpdate = useProcessRowUpdate;
